import { useCallback, useState } from 'react'
import { Copy, Check } from 'lucide-react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import { Light as SyntaxHighlighter } from 'react-syntax-highlighter'
import { atomOneDark } from 'react-syntax-highlighter/dist/esm/styles/hljs'
import js from 'react-syntax-highlighter/dist/esm/languages/hljs/javascript'
import python from 'react-syntax-highlighter/dist/esm/languages/hljs/python'
import bash from 'react-syntax-highlighter/dist/esm/languages/hljs/bash'
import json from 'react-syntax-highlighter/dist/esm/languages/hljs/json'
import typescript from 'react-syntax-highlighter/dist/esm/languages/hljs/typescript'
import { haptics } from '../../lib/haptics'
import { parseThinking, parseStreamingThinking } from '../../lib/parse-thinking'
import ThinkingBlock from './ThinkingBlock'

SyntaxHighlighter.registerLanguage('javascript', js)
SyntaxHighlighter.registerLanguage('js', js)
SyntaxHighlighter.registerLanguage('typescript', typescript)
SyntaxHighlighter.registerLanguage('ts', typescript)
SyntaxHighlighter.registerLanguage('python', python)
SyntaxHighlighter.registerLanguage('bash', bash)
SyntaxHighlighter.registerLanguage('sh', bash)
SyntaxHighlighter.registerLanguage('json', json)

interface Props {
  role: 'user' | 'assistant'
  content: string
  streaming?: boolean
  model?: string
  costHint?: string
  /** Base64 image thumbnail for user messages with image attachment */
  imageThumbnail?: string
  /** Whether this is a Secure (Cocoon TEE) model — affects thinking block colors */
  isSecure?: boolean
  /** Duration of thinking in seconds (populated after streaming completes) */
  thinkingDuration?: number
}

/** Copy button for code blocks */
function CodeCopyButton({ code }: { code: string }) {
  const [copied, setCopied] = useState(false)
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code)
      haptics.notification('success')
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      haptics.notification('error')
    }
  }
  return (
    <button
      onClick={handleCopy}
      className="text-[11px] px-2 py-0.5 rounded active:opacity-60 transition-opacity"
      style={{
        color: 'var(--tg-theme-hint-color)',
        backgroundColor: 'var(--tg-theme-secondary-bg-color)',
      }}
    >
      {copied ? '✓' : 'Copy'}
    </button>
  )
}

/** Custom code block renderer with syntax highlighting and copy button.
 *  ReactMarkdown calls this for fenced code blocks (``` ... ```).
 *  We receive className="language-python" etc. on the <code> element.
 *
 *  wrapLongLines is intentionally NOT set — long lines scroll horizontally
 *  instead of wrapping, preserving code formatting. */
function CodeBlock({
  className,
  children,
}: {
  className?: string
  children?: React.ReactNode
}) {
  const code = typeof children === 'string' ? children.replace(/\n$/, '') : ''
  // Extract language from className="language-python"
  const lang = className?.replace('language-', '') ?? ''

  return (
    <div
      className="rounded-[10px] my-2"
      style={{
        border: '1px solid rgba(255,255,255,0.06)',
        overflow: 'hidden', // clips to rounded corners
      }}
    >
      {/* Header bar */}
      <div
        className="flex items-center justify-between px-3 py-1.5"
        style={{ backgroundColor: 'rgba(0,0,0,0.35)' }}
      >
        <span className="text-[11px]" style={{ color: 'rgba(255,255,255,0.45)' }}>
          {lang || 'code'}
        </span>
        {code && <CodeCopyButton code={code} />}
      </div>
      {/* SyntaxHighlighter scrolls horizontally — no line-wrap, proper code layout */}
      <SyntaxHighlighter
        language={lang || 'text'}
        style={atomOneDark}
        customStyle={{
          margin: 0,
          borderRadius: 0,
          fontSize: '12px',
          padding: '10px 12px',
          background: '#1e2127',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {code}
      </SyntaxHighlighter>
    </div>
  )
}

/** Wrapper for <pre> — plain blocks without language fence fall back here */
function PreBlock({ children }: { children?: React.ReactNode }) {
  const child = children as React.ReactElement<{ className?: string; children?: React.ReactNode }>
  // If child is a <code> with a language class, delegate to CodeBlock
  if (child?.props?.className?.startsWith('language-')) {
    return (
      <CodeBlock className={child.props.className}>
        {child.props.children}
      </CodeBlock>
    )
  }
  // Plain pre block without language
  const code = typeof child?.props?.children === 'string' ? child.props.children : ''
  return (
    <div
      className="rounded-[10px] my-2"
      style={{
        border: '1px solid var(--tg-theme-secondary-bg-color)',
        overflow: 'hidden',
      }}
    >
      <div
        className="flex items-center justify-between px-3 py-1.5"
        style={{ backgroundColor: 'var(--tg-theme-secondary-bg-color)' }}
      >
        <span className="text-[11px]" style={{ color: 'var(--tg-theme-hint-color)' }}>code</span>
        {code && <CodeCopyButton code={code} />}
      </div>
      <pre
        style={{ backgroundColor: 'var(--tg-theme-bg-color)', margin: 0, border: 'none' }}
        className="overflow-x-auto px-3 py-2.5 text-[12px]"
      >
        {children}
      </pre>
    </div>
  )
}

export default function ChatBubble({ role, content, streaming, model, costHint, imageThumbnail, isSecure, thinkingDuration }: Props) {
  const isUser = role === 'user'
  const [copied, setCopied] = useState(false)

  const handleCopy = useCallback(async () => {
    try {
      // Copy only the main content (strip thinking)
      const { content: mainContent } = parseThinking(content)
      await navigator.clipboard.writeText(mainContent)
      haptics.notification('success')
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    } catch {
      haptics.notification('error')
    }
  }, [content])

  // Typing indicator — shown while waiting for first token (streaming=true, content='')
  if (!isUser && streaming && content === '') {
    return (
      <div className="flex items-start mb-sp-2">
        <div
          className="rounded-2xl px-4 py-3"
          style={{
            backgroundColor: 'var(--tg-theme-secondary-bg-color)',
            borderBottomLeftRadius: 'var(--space-1)',
          }}
        >
          {isSecure ? (
            // Secure model: enhanced typing with status text
            <div className="min-w-[140px]">
              <div className="flex items-center gap-1.5">
                <span className="typing-dot" style={{ animationDelay: '0ms', backgroundColor: '#A062FF' }} />
                <span className="typing-dot" style={{ animationDelay: '160ms', backgroundColor: '#3088FF' }} />
                <span className="typing-dot" style={{ animationDelay: '320ms', backgroundColor: '#61D8FF' }} />
              </div>
              <p className="text-[13px] mt-sp-2" style={{ color: 'var(--tg-theme-hint-color)' }}>
                Private processing…
              </p>
            </div>
          ) : (
            <div className="flex items-center gap-1.5">
              <span className="typing-dot" style={{ animationDelay: '0ms' }} />
              <span className="typing-dot" style={{ animationDelay: '160ms' }} />
              <span className="typing-dot" style={{ animationDelay: '320ms' }} />
            </div>
          )}
        </div>
      </div>
    )
  }

  // Parse thinking for assistant messages
  const streamingParsed = !isUser && streaming ? parseStreamingThinking(content) : null
  const completeParsed = !isUser && !streaming ? parseThinking(content) : null

  return (
    <div className={`flex flex-col ${isUser ? 'items-end' : 'items-start'} mb-sp-2`}>
      <div
        className="max-w-[85%] rounded-2xl px-sp-4 py-sp-2 text-[15px] leading-relaxed"
        style={
          isUser
            ? {
                backgroundColor: 'var(--tg-theme-button-color)',
                color: 'var(--tg-theme-button-text-color)',
                borderBottomRightRadius: 'var(--space-1)',
              }
            : {
                backgroundColor: 'var(--tg-theme-secondary-bg-color)',
                color: 'var(--tg-theme-text-color)',
                borderBottomLeftRadius: 'var(--space-1)',
              }
        }
      >
        {isUser ? (
          // User messages: image thumbnail (if any) + plain text
          <>
            {imageThumbnail && (
              <img
                src={imageThumbnail}
                alt="attachment"
                className="rounded-xl mb-1 max-w-full"
                style={{ maxHeight: '200px', objectFit: 'cover' }}
              />
            )}
            {content && <p className="whitespace-pre-wrap">{content}</p>}
          </>
        ) : streaming ? (
          // Assistant streaming — handle <think> blocks
          streamingParsed?.isThinking ? (
            // Still inside <think> — show ThinkingBlock expanded
            <ThinkingBlock
              content={streamingParsed.thinkingContent}
              isStreaming
              isSecure={isSecure}
            />
          ) : streamingParsed?.thinkingContent ? (
            // <think> closed — show collapsed thinking + streaming content
            <>
              <ThinkingBlock
                content={streamingParsed.thinkingContent}
                isSecure={isSecure}
                duration={thinkingDuration}
              />
              <p className="whitespace-pre-wrap streaming-cursor">
                {streamingParsed.visibleContent}
              </p>
            </>
          ) : (
            // No <think> — normal streaming
            <p className="whitespace-pre-wrap streaming-cursor">{content}</p>
          )
        ) : (
          // Assistant complete: full markdown rendering
          <div className="prose-chat">
            {completeParsed?.thinking && (
              <ThinkingBlock
                content={completeParsed.thinking}
                isSecure={isSecure}
                duration={thinkingDuration}
              />
            )}
            <ReactMarkdown
              remarkPlugins={[remarkGfm]}
              components={{
                pre: PreBlock,
              }}
            >
              {completeParsed?.content ?? content}
            </ReactMarkdown>
          </div>
        )}

        {/* Footer: model label + copy button — only when complete */}
        {!isUser && !streaming && content.length > 0 && (
          <div className="flex items-center justify-between mt-1.5 gap-2">
            {model ? (
              <p className="text-[11px] font-mono opacity-50">{model}</p>
            ) : (
              <span />
            )}
            <button
              onClick={handleCopy}
              className="shrink-0 opacity-[0.55] active:opacity-70 transition-opacity"
              style={{ color: 'var(--tg-theme-text-color)' }}
            >
              {copied ? <Check size={15} /> : <Copy size={15} />}
            </button>
          </div>
        )}
      </div>

      {/* Cost hint — shown below assistant bubble after streaming completes */}
      {!isUser && !streaming && costHint && (
        <div className="mt-0.5 px-1">
          <span
            className="text-[11px] tabular-nums"
            style={{ color: 'var(--tg-theme-hint-color)' }}
          >
            {costHint}
          </span>
        </div>
      )}
    </div>
  )
}
